/**
 * Notifications hook — backed by the real /api/v1/notifications endpoints.
 *
 * Historical context: this file used to be a zustand mock with hardcoded
 * seed data and an `addNotification()` method that the UI called directly
 * to simulate cross-role pings. As of 2026-04-28 the backend creates
 * notifications server-side from the originating business operation
 * (allocations, etc.); the frontend now polls the API and the local
 * store is read-only.
 *
 * The exported hook signature is preserved so existing call sites continue
 * to compile; `addNotification` is now a no-op (server is the source of
 * truth). Static `useNotificationsStore.getState()` is supported for the
 * existing call sites but only exposes no-op fns — convert to the hook
 * form when touching that code.
 */
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import {
  fetchMyNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  type ServerNotification,
  type NotificationPriority,
} from './notificationsApi';

export type NotifType =
  // Brief / allocation lifecycle
  | 'brief_received'
  | 'brief_accepted'
  | 'brief_returned'
  | 'brief_declined'
  | 'brief_withdrawn'
  // Conference lifecycle
  | 'conference_requested'
  | 'conference_scheduled'
  | 'conference_cancelled'
  | 'conference_completed'
  // Hearings
  | 'hearing_booked'
  | 'hearing_assigned'
  | 'hearing_reminder'
  | 'hearing_outcome_pending'
  // Intake
  | 'intake_received'
  // Matters / cases
  | 'matter_created'
  | 'matter_needs_allocation'
  | 'case_assigned'
  | 'matter_inactive'
  | 'conflict_check_required'
  // Tasks
  | 'task_assigned'
  | 'task_due'
  // Documents & fees
  | 'document_uploaded'
  | 'fee_note_issued'
  | 'fee_note_paid'
  | 'fee_note_pending'
  // Compliance
  | 'kyc_expiring'
  | 'limitation_approaching'
  | 'judgment_reserved'
  // Team / clerk assignment
  | 'clerk_assigned'
  | 'clerk_unassigned'
  // Messaging & misc
  | 'message_received'
  | 'diary_updated'
  | 'policy_published'
  | 'clerk_coordination_requested'
  | 'clerk_coordination_updated'
  | 'clerk_coordination_cancelled'
  | 'system'
  | 'general';

/**
 * UI-facing Notification shape. Kept in sync with the legacy mock so
 * NotificationListener and dashboard widgets do not need to change.
 */
export interface Notification {
  id: string;
  /** Role this notification targets — derived from the current user. */
  forRole: 'clerk' | 'barrister' | 'admin';
  /** Surname of the recipient — derived from the current user. */
  forName?: string;
  /** Sender display name. Backend does not track this; '' for now. */
  fromName: string;
  fromRole: 'clerk' | 'barrister' | 'admin';
  type: NotifType;
  title: string;
  detail: string;
  link?: string;
  /** Entity type for navigation (e.g., 'Case', 'Hearing', 'Message') */
  entityType?: string;
  /** Entity ID for navigation */
  entityId?: string;
  priority: NotificationPriority;
  read: boolean;
  createdAt: number;
  /** ISO of the original server row, kept for re-fetch / debugging. */
  serverCreatedAt?: string;
}

interface State {
  notifications: Notification[];
  /** No-op since 2026-04-28 — server creates notifications. Logged in dev. */
  addNotification: (input: Partial<Notification>) => void;
  markRead: (id: string) => void;
  markAllRead: (forRole?: string, forName?: string) => void;
  dismiss: (id: string) => void;
  getForClerk: (clerkName: string) => Notification[];
  getForBarrister: (name: string) => Notification[];
  getUnreadCount: (forRole: string, forName?: string) => number;
}

const POLL_INTERVAL_MS = 120_000; // 120s fallback — WebSocket push is the primary signal
const NOTIFS_KEY = ['notifications'] as const;
const UNREAD_KEY = ['notifications', 'unread-count'] as const;

function adapt(server: ServerNotification): Notification {
  return {
    id: server.id,
    // role/name fields exist for legacy UI filtering — server already
    // scoped the list to the current user, so any value is correct.
    forRole: 'clerk',
    forName: undefined,
    fromName: '',
    fromRole: 'admin',
    type: (server.type as NotifType) ?? 'system',
    priority: server.priority ?? 'normal',
    title: server.title,
    detail: server.body ?? '',
    link: server.link ?? undefined,
    entityType: server.entityType ?? undefined,
    entityId: server.entityId ?? undefined,
    read: server.readAt !== null,
    createdAt: new Date(server.createdAt).getTime(),
    serverCreatedAt: server.createdAt,
  };
}

const noopWarned = new Set<string>();
function devWarnNoop(method: string): void {
  if (import.meta.env.DEV && !noopWarned.has(method)) {
    noopWarned.add(method);
    // eslint-disable-next-line no-console
    console.warn(
      `[notifications] ${method}() is a no-op — notifications are now created server-side. Remove this call site when convenient.`,
    );
  }
}

export function useNotificationsStore<T>(selector: (s: State) => T): T {
  const qc = useQueryClient();

  // /notifications is chambers-scoped and correctly refuses an external solicitor. The
  // portal renders the same AppShell as the chambers app, so without this guard every
  // solicitor page polled an endpoint that can only ever answer 403 - twice a minute,
  // with a console error each time. They are not being denied anything they should have:
  // the portal exposes no notifications route at all.
  const isExternal = useAuthStore((st) => st.user?.memberType) === 'solicitor';

  const { data: rawList = [] } = useQuery({
    queryKey: NOTIFS_KEY,
    queryFn: () => fetchMyNotifications({ limit: 50 }).then((r) => r.notifications),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false, // stop polling when tab is hidden
    enabled: !isExternal,
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: fetchUnreadCount,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    enabled: !isExternal,
  });

  // Stable refs across renders. Without these, every parent render rebuilds
  // a fresh notifications array + new function refs, which downstream
  // useEffect deps interpret as a change and re-fire — and combined with
  // React Query's invalidation cycle could be misread as a polling loop.
  const notifications = useMemo(() => rawList.map(adapt), [rawList]);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: NOTIFS_KEY });
    qc.invalidateQueries({ queryKey: UNREAD_KEY });
  }, [qc]);

  const markRead = useCallback(
    (id: string) => {
      void markNotificationRead(id).then(invalidate).catch(() => undefined);
    },
    [invalidate],
  );

  const markAllRead = useCallback(() => {
    void markAllNotificationsRead().then(invalidate).catch(() => undefined);
  }, [invalidate]);

  const dismiss = useCallback(
    (id: string) => {
      void dismissNotification(id).then(invalidate).catch(() => undefined);
    },
    [invalidate],
  );

  const addNotification = useCallback(
    () => devWarnNoop('addNotification'),
    [],
  );

  const getForClerk = useCallback(() => notifications, [notifications]);
  const getForBarrister = useCallback(() => notifications, [notifications]);
  const getUnreadCount = useCallback(() => unreadCount, [unreadCount]);

  const state = useMemo<State>(
    () => ({
      notifications,
      addNotification,
      markRead,
      markAllRead,
      dismiss,
      getForClerk,
      getForBarrister,
      getUnreadCount,
    }),
    [
      notifications,
      addNotification,
      markRead,
      markAllRead,
      dismiss,
      getForClerk,
      getForBarrister,
      getUnreadCount,
    ],
  );

  return selector(state);
}

/**
 * Static accessor preserved for the few call sites that use
 * `useNotificationsStore.getState().addNotification(...)`. All methods
 * here are no-ops — convert to the hook form when touching that code.
 */
useNotificationsStore.getState = (): State => ({
  notifications: [],
  addNotification: () => devWarnNoop('addNotification (static)'),
  markRead: () => devWarnNoop('markRead (static)'),
  markAllRead: () => devWarnNoop('markAllRead (static)'),
  dismiss: () => devWarnNoop('dismiss (static)'),
  getForClerk: () => [],
  getForBarrister: () => [],
  getUnreadCount: () => 0,
});
